import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";

import { resolveAnchorElement } from "../../services/intent/anchorTargets";
import { NudgeAction } from "../../services/intent/types";
import AppButton from "../reusableComponent/button/button";
import styles from "./IntentInlineBanner.module.scss";

interface IntentInlineBannerProps {
  nudge: NudgeAction;
  onDismiss: () => void;
  onCta: () => void;
}

const MAX_RETRY_FRAMES = 30;
const TOP_SAFE_AREA_PX = 64;

const resolveWithTopFallback = (
  rawTarget: string,
): HTMLElement | null => {
  const primary = resolveAnchorElement(rawTarget);
  if (!primary) return null;
  if (!rawTarget.endsWith("-top")) return primary;
  if (primary.getBoundingClientRect().top >= TOP_SAFE_AREA_PX) return primary;
  const fallback = resolveAnchorElement(rawTarget.replace(/-top$/, ""));
  return fallback ?? primary;
};

const toneClass = (tone?: string): string => {
  switch (tone) {
    case "success":
      return styles.toneSuccess;
    case "error":
      return styles.toneError;
    case "info":
    default:
      return styles.toneInfo;
  }
};

const IntentInlineBanner: React.FC<IntentInlineBannerProps> = ({
  nudge,
  onDismiss,
  onCta,
}) => {
  const [target, setTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!nudge.target) {
      console.warn(`[intent] inline-banner missing target id=${nudge.id}`);
      onDismiss();
      return;
    }
    let frame = 0;
    let raf = 0;
    const tick = (): void => {
      const el = resolveWithTopFallback(nudge.target as string);
      if (el) {
        setTarget(el);
        return;
      }
      frame += 1;
      if (frame > MAX_RETRY_FRAMES) {
        console.warn(
          `[intent] inline-banner target not found target=${nudge.target} id=${nudge.id}`,
        );
        onDismiss();
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [nudge.target, nudge.id, onDismiss]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onDismiss();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onDismiss]);

  useEffect(() => {
    if (!target || !nudge.sticky) return;
    target.classList.add(styles.stickyMarker);
    return () => target.classList.remove(styles.stickyMarker);
  }, [target, nudge.sticky]);

  if (!target) return null;

  const banner = (
    <div
      className={`${styles.banner} ${toneClass(nudge.tone)}`}
      role="status"
      aria-live="polite"
    >
      <div className={styles.text}>{nudge.text}</div>
      {nudge.cta?.label ? (
        <AppButton variant="newTertiary" size="sm" onClick={onCta} className={styles.cta}>
          {nudge.cta.label}
        </AppButton>
      ) : (
        <button
          type="button"
          className={styles.closeBtn}
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          <CloseOutlinedIcon sx={{ fontSize: 20 }} />
        </button>
      )}
    </div>
  );

  return createPortal(banner, target);
};

export default IntentInlineBanner;
